import { FarmlyAvatar } from '@/components/farmly/FarmlyAvatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/context/AuthContext';
import { useMyPortals } from '@/hooks/useMyPortals';
import { useMyProducers } from '@/hooks/useMyProducers';
import { useMyProfile } from '@/hooks/useMyProfile';

export function DashboardHeader() {
  const { user, roles, rolesLoading } = useAuth();
  const { isEditor } = useMyPortals();
  const { producers } = useMyProducers();
  const { profile } = useMyProfile();

  const userInitials = profile?.display_name
    ? profile.display_name.substring(0, 2).toUpperCase()
    : user?.email
      ? user.email.substring(0, 2).toUpperCase()
      : 'AN';

  const editorPlaceCount = producers?.length ?? 0;

  const subtitle = isEditor && editorPlaceCount > 0
    ? `Du redigerar ${editorPlaceCount} plats${editorPlaceCount > 1 ? 'er' : ''} i Farmly.`
    : 'Här hittar du bokningar, sparade platser och konto.';

  const roleBadgeLabel = (role: string) => {
    const labels: Record<string, string> = {
      admin: 'Administratör',
      user: 'Besökare',
      producer: 'Värd',
    };
    return labels[role] || role;
  };

  return (
    <section>
      <p className="text-xs text-muted-foreground/60 tracking-wide">
        {profile?.display_name
          ? `Välkommen tillbaka, ${profile.display_name}.`
          : 'Välkommen tillbaka.'}
      </p>
      <div className="flex items-center gap-4 mt-1">
        {(profile?.avatar_url || user?.user_metadata?.avatar_url)?.startsWith('http') ? (
          <img
            src={profile?.avatar_url || user?.user_metadata?.avatar_url}
            alt="Profilbild"
            referrerPolicy="no-referrer"
            crossOrigin="anonymous"
            className="h-16 w-16 rounded-full border border-[#D8D2CA] object-cover shrink-0"
          />
        ) : (
          <FarmlyAvatar
            imageUrl={profile?.avatar_url || user?.user_metadata?.avatar_url}
            initials={userInitials}
            size="lg"
            className="shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold tracking-tight text-foreground">
              Din Farmly
            </h1>
            {rolesLoading ? (
              <Skeleton className="h-5 w-16" />
            ) : (
              roles.map((role) => (
                <Badge
                  key={role}
                  variant="secondary"
                  className={`text-[10px] font-normal ${role === 'producer' ? 'bg-primary/10 text-primary border-primary/20' : ''}`}
                >
                  {roleBadgeLabel(role)}
                </Badge>
              ))
            )}
          </div>
          {user?.email && (
            <p className="text-xs text-muted-foreground/50 mt-0.5 truncate">
              {user.email}
            </p>
          )}
          <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
            {subtitle}
          </p>
        </div>
      </div>
    </section>
  );
}
